import { useTranslation } from "react-i18next";
import { contactLinks, displayPhone, isMobilePhone, normalizePhone, type WorkshopContacts } from "../../../../supabase/functions/_shared/messaging";

/**
 * The workshop's contacts on every customer page (#quote, #intake, #ready; owner, 2026-11-04: the customer never has
 * to copy anything): the phone with Call (and SMS for a mobile), the e-mail as a mailto link, the workshop address with
 * Google Maps and Waze. Big buttons for the thumb; a line without data is left out, nothing shows without any data.
 */
export function WorkshopContactsBlock({ contacts }: { contacts: WorkshopContacts | null | undefined }) {
  const { t } = useTranslation();
  const l = contactLinks(contacts);
  if (!l.phone && !l.email && !l.address) return null;
  return (
    <section className="contacts-box" aria-label={t("contacts.title")}>
      <h2>{t("contacts.title")}</h2>
      {l.phone && (
        <div className="cb-row">
          <span className="cb-cap">{t("contacts.phone")}</span>
          <a className="cb-value mono" href={l.phone.tel}>{l.phone.display}</a>
          <div className="cb-buttons">
            <a className="button primary" href={l.phone.tel}>{t("contacts.call")}</a>
            {l.phone.sms && <a className="button" href={l.phone.sms}>{t("contacts.sms")}</a>}
          </div>
        </div>
      )}
      {l.email && (
        <div className="cb-row">
          <span className="cb-cap">{t("contacts.email")}</span>
          <a className="cb-value" href={l.email.mailto}>{l.email.address}</a>
        </div>
      )}
      {l.address && (
        <div className="cb-row">
          <span className="cb-cap">{t("contacts.address")}</span>
          <span className="cb-value">{l.address.text}</span>
          <div className="cb-buttons cb-route">
            <a className="button" href={l.address.google} target="_blank" rel="noreferrer">{t("contacts.google")}</a>
            <a className="button" href={l.address.waze} target="_blank" rel="noreferrer">{t("contacts.waze")}</a>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * A customer's contacts as one tap links (the Reminders page): call the phone and the mobile, SMS to a mobile (the
 * mobile field, or a phone that is a mobile number), e-mail. Nothing for what is missing.
 */
export function CustomerContactLinks({ name, phone, mobile, email }: { name: string | null; phone?: string | null; mobile?: string | null; email?: string | null }) {
  const { t } = useTranslation();
  const links: { key: string; href: string; label: string; value?: string }[] = [];
  const add = (raw: string | null | undefined, smsAlways: boolean) => {
    const n = raw ? normalizePhone(raw) : null;
    if (!n || links.some((x) => x.href === `tel:+${n}`)) return;
    links.push({ key: `tel-${n}`, href: `tel:+${n}`, label: t("contacts.call"), value: displayPhone(raw) ?? raw ?? "" });
    if (smsAlways || isMobilePhone(raw)) links.push({ key: `sms-${n}`, href: `sms:+${n}`, label: t("contacts.sms") });
  };
  add(mobile, true);
  add(phone, false);
  const mail = email?.trim();
  if (mail && /^[^\s@<>"'(),;:]+@[^\s@<>"'(),;:]+\.[^\s@<>"'(),;:]+$/.test(mail)) links.push({ key: "mail", href: `mailto:${mail}`, label: t("contacts.email"), value: mail });
  if (links.length === 0) return null;
  return (
    <div className="contact-links" role="group" aria-label={t("contacts.customerLinks", { name: name ?? "" })}>
      {links.map((x) => (
        <a key={x.key} className="button" href={x.href}>
          <span className="cl-label">{x.label}</span>
          {x.value && <span className={x.key === "mail" ? "cl-value cl-mail" : "cl-value"}>{x.value}</span>}
        </a>
      ))}
    </div>
  );
}
